/** Canvas rendering for migrated designs.
 *  A design listed in /designs-config.json renders on the client: the
 *  encrypted background is decrypted in memory (never exposed as a file),
 *  the user photos and texts are drawn with the same rules the .NET
 *  service uses, and the result is handed back in the server response
 *  shape so the rest of the flow (display, share, history) is unchanged.
 */
import config from "./config.js";

// prototype key parts - the production key delivery is a separate step
const _k1 = "wael";
const _k2 = "sayed";

const REGISTRY_URL = "/designs-config.json";
const FONT_PATH = "/fonts/Arabic-Fonts/";
const STORE_PREFIX = "canvasimg:";
const STORE_INDEX = "canvasimg:index";
const STORE_MAX = 5;

let registryPromise = null;
const loadedFonts = new Set();

/** Load the designs registry once and return the entry for a design. */
export async function getDesignConfig(designName) {
  if (!registryPromise) {
    registryPromise = fetch(REGISTRY_URL)
      .then((res) => (res.ok ? res.json() : {}))
      .catch(() => ({}));
  }
  const registry = await registryPromise;
  return registry[designName] || null;
}

/** Tell whether a registry entry renders on the client (RenderMode is not "Server"). */
export function isCanvasEntry(entry) {
  return !!entry && entry.RenderMode !== "Server";
}

/** Check the local store for a design restored by the page's own record. */
export async function getCachedState(designName, imageName) {
  const entry = await getDesignConfig(designName);
  if (!isCanvasEntry(entry)) return null;
  return getStoredImage(imageName) ? "stored" : "missing";
}

/** Return the stored image data for a locally rendered file name. */
export function getStoredImage(imageName) {
  try {
    return localStorage.getItem(STORE_PREFIX + imageName);
  } catch (e) {
    return null;
  }
}

/** Keep the rendered image data so a refresh restores it without rendering again. */
function persistRendered(name, blob) {
  try {
    const reader = new FileReader();
    reader.onload = function () {
      let idx = JSON.parse(localStorage.getItem(STORE_INDEX) || "[]");
      idx = idx.filter((n) => n !== name);
      idx.push(name);
      while (idx.length > STORE_MAX) {
        localStorage.removeItem(STORE_PREFIX + idx.shift());
      }
      // on a full store, evict the oldest renders until the new one fits
      for (;;) {
        try {
          localStorage.setItem(STORE_PREFIX + name, reader.result);
          break;
        } catch (e) {
          if (idx.length <= 1) return; // cannot free enough: give up silently
          localStorage.removeItem(STORE_PREFIX + idx.shift());
        }
      }
      try {
        localStorage.setItem(STORE_INDEX, JSON.stringify(idx));
      } catch (e) { /* best-effort */ }
    };
    reader.readAsDataURL(blob);
  } catch (e) { /* best-effort */ }
}

/** Drop a stored render (used when its history entry is deleted). */
export function removeStoredImage(imageName) {
  try {
    localStorage.removeItem(STORE_PREFIX + imageName);
    let idx = JSON.parse(localStorage.getItem(STORE_INDEX) || "[]");
    idx = idx.filter((n) => n !== imageName);
    localStorage.setItem(STORE_INDEX, JSON.stringify(idx));
  } catch (e) { /* best-effort */ }
}

/** Derive the decryption key from the passphrase parts. */
async function deriveKey(salt) {
  const material = await crypto.subtle.importKey(
    "raw", new TextEncoder().encode(_k1 + _k2), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 100000, hash: "SHA-256" },
    material, { name: "AES-GCM", length: 256 }, false, ["decrypt"]);
}

/** Detect an image MIME type from its magic bytes (png, jpeg or webp). */
function imageType(bytes) {
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "image/png";
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  const riff = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46;
  const webp = bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
  if (riff && webp) return "image/webp";
  return "image/png";
}

/** Decrypt a TSD1 payload and decode it as an image bitmap. */
async function decryptToBitmap(bytes) {
  const salt = bytes.slice(5, 21);
  const iv = bytes.slice(21, 33);
  const key = await deriveKey(salt);
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, bytes.slice(33));
  return createImageBitmap(new Blob([plain], { type: imageType(new Uint8Array(plain)) }));
}

/** Fetch and decrypt the design background for a variant (pv<N> -> av<N>). */
async function loadBackground(designName, entry, imageSystem) {
  const variant = (imageSystem || "pv1").replace("pv", "av");
  let base;
  if (entry.AvPath) {
    base = "/" + String(entry.AvPath).replace(/^images\//, "") + "/";
  } else {
    base = "/imgs-app/" + designName + "/av/";
  }
  // assets are unified to webp; legacy extensions stay as fallbacks for
  // designs not converted yet. Accept the first response that actually
  // carries an encrypted TSD1 payload.
  const extensions = [".webp", ".png", ".jpg", ".jpeg"];
  for (const ext of extensions) {
    const res = await fetch(base + variant + ext);
    if (!res.ok) continue;
    const bytes = new Uint8Array(await res.arrayBuffer());
    const isTsd1 = bytes[0] === 0x54 && bytes[1] === 0x53 && bytes[2] === 0x44 && bytes[3] === 0x31;
    if (isTsd1) return decryptToBitmap(bytes);
  }
  throw new Error("background fetch failed");
}

/** Load a design font by its file name if it is not loaded yet. */
async function ensureFont(fileName) {
  if (!fileName) return;
  const family = fileName.replace(/\.[^.]+$/, "");
  if (loadedFonts.has(family)) return;
  const face = new FontFace(family, "url(" + FONT_PATH + encodeURIComponent(fileName) + ")");
  await face.load();
  document.fonts.add(face);
  loadedFonts.add(family);
}

/** Decode one user photo payload entry into an image bitmap. */
async function loadPhoto(raw) {
  const url = raw.indexOf("data:") === 0 ? raw : "data:image/jpeg;base64," + raw;
  const blob = await (await fetch(url)).blob();
  return createImageBitmap(blob);
}

/** Draw one user photo into its overlay rectangle (stretch, radius, opacity). */
function drawPhoto(ctx, bitmap, overlay) {
  const x = overlay.PositionX, y = overlay.PositionY;
  const w = overlay.Width, h = overlay.Height;
  ctx.save();
  if (overlay.opacity != null) ctx.globalAlpha = overlay.opacity;
  ctx.beginPath();
  if (overlay.IsCircular) {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else if (overlay.Raduis > 0) {
    ctx.roundRect(x, y, w, h, overlay.Raduis);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.clip();
  ctx.drawImage(bitmap, x, y, w, h);
  ctx.restore();
}

/** Resolve the effective settings object for one text. */
function textSettings(entry, titleEntry) {
  if (titleEntry || entry.TitleSharedSetting) {
    return Object.assign({}, entry.TitleSharedSetting || {}, titleEntry || {});
  }
  return entry;
}

/** Build the list of texts to draw from the payload and the design entry. */
function collectTexts(payload, entry) {
  const texts = [];
  let ct = null;
  if (payload.Ct) {
    try { ct = JSON.parse(payload.Ct); } catch (e) { ct = null; }
  }
  if (Array.isArray(ct) && ct.length) {
    ct.forEach((item) => {
      const titleEntry = (entry.Titles || []).find((t) => t.Name === item.n) || null;
      texts.push({ value: item.t || "", settings: textSettings(entry, titleEntry) });
    });
    return texts;
  }
  if (payload.Title) {
    const first = entry.Titles && entry.Titles.length ? entry.Titles[0] : null;
    texts.push({ value: payload.Title, settings: textSettings(entry, first) });
  }
  return texts;
}

/** Draw one text with the .NET rules (shrink to width, shadow, border, stroke). */
function drawText(ctx, value, s) {
  if (!value) return;
  const arabic = /[\u0600-\u06FF]/.test(value);
  const fontFile = arabic ? s.FontArabicName : s.FontEnglishName;
  const family = (fontFile || "").replace(/\.[^.]+$/, "");
  let size = arabic ? s.FontArabicSize : s.FontEnglishSize;
  if (!size) return;
  const decrease = arabic ? s.IsDecreaseArabicFontSize : s.IsDecreaseEnglishFontSize;
  const setFont = () => { ctx.font = size + "px \"" + family + "\""; };
  setFont();
  if (decrease && s.MaxTextWidth) {
    // .NET GetDynamicFontSize: shrink until the text fits MaxTextWidth
    while (size > 1 && ctx.measureText(value).width > s.MaxTextWidth) {
      size--;
      setFont();
    }
  } else if (decrease && s.MaxFontSize) {
    // .NET GetFontSizeByTitleLength: MaxFontSize is a character-count
    // threshold, not a pixel size - every character past it costs
    // MultiplePixels of font size. Dates (three dash-separated parts)
    // never shrink.
    const isDate = value.indexOf("-") > -1 && value.split("-").length === 3;
    if (!isDate && value.length > s.MaxFontSize) {
      size -= (value.length - s.MaxFontSize) * (s.MultiplePixels || 0);
      if (size < 1) size = 1;
      setFont();
    }
  }
  let x = s.PositionX;
  if (s.IsDynamicPositionX && !arabic && s.PositionXL != null) x = s.PositionXL;
  const y = s.PositionY;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "bevel";
  if (s.IsHasShadow) {
    ctx.fillStyle = s.ShadowColor || "#000";
    ctx.fillText(value, x + 5, y + 5);
  }
  if (s.PrimaryBorderSize) {
    ctx.strokeStyle = s.PrimaryColorLine || "#000";
    ctx.lineWidth = s.PrimaryBorderSize * 2;
    ctx.strokeText(value, x, y);
  }
  if (s.IsHasStroke && s.StrokeBorderSize) {
    ctx.strokeStyle = s.StrokeColorLine || "#000";
    ctx.lineWidth = s.StrokeBorderSize * 2;
    ctx.strokeText(value, x, y);
  }
  ctx.fillStyle = s.FontColor || "#fff";
  ctx.fillText(value, x, y);
}

/** Build a validation error: the caller must surface it, never fall back. */
function validationError(reason) {
  const err = new Error("canvas validation failed: " + reason);
  err.isValidationError = true;
  return err;
}

/** Check the payload carries everything this design expects (texts, photos). */
function validatePayload(payload, entry) {
  const expectsText =
    (entry.Titles && entry.Titles.length) ||
    entry.TitleSharedSetting ||
    entry.FontArabicName ||
    entry.FontEnglishName;
  if (expectsText) {
    let ct = null;
    if (payload.Ct) {
      try { ct = JSON.parse(payload.Ct); } catch (e) { ct = null; }
    }
    if (entry.Titles && entry.Titles.length) {
      // a multi-title design is served by the composite text payload:
      // every title in the design object must arrive with it
      if (!Array.isArray(ct) || ct.length < entry.Titles.length) {
        throw validationError("some texts this design expects did not arrive");
      }
    }
    const texts = collectTexts(payload, entry);
    if (!texts.length) {
      throw validationError("the design expects a text but none arrived");
    }
    texts.forEach((t) => {
      if (!t.value || !String(t.value).trim()) {
        throw validationError("a text this design expects arrived empty");
      }
    });
  }
  const overlays = entry.OverlayImages || [];
  overlays.forEach((overlay, i) => {
    const raw = payload.FileRaw && payload.FileRaw[i] && payload.FileRaw[i].Raw;
    if (!raw) throw validationError("a photo this design expects did not arrive");
  });
}

/** Random file name in the same shape the server returns (guid + .jpg). */
function fakeName() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("") + ".jpg";
}

/** Render a design locally from the same payload the server flow builds. */
export async function renderDesign(payload, entry) {
  validatePayload(payload, entry);
  const background = await loadBackground(payload.DesignName, entry, payload.imageSystem);
  const width = entry.Width || background.width;
  const height = entry.Height || background.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  const overlays = (entry.OverlayImages || []).slice().sort((a, b) => (a.Index || 0) - (b.Index || 0));
  const photos = [];
  for (let i = 0; i < overlays.length; i++) {
    const raw = payload.FileRaw && payload.FileRaw[i] && payload.FileRaw[i].Raw;
    if (!raw) { photos.push(null); continue; }
    try {
      photos.push(await loadPhoto(raw));
    } catch (e) {
      throw validationError("a photo this design expects could not be decoded");
    }
  }
  const drawLayer = (index) => {
    overlays.forEach((overlay, i) => {
      if ((overlay.Index || 0) === index && photos[i]) drawPhoto(ctx, photos[i], overlay);
    });
  };
  drawLayer(0);
  ctx.drawImage(background, 0, 0, width, height);
  drawLayer(1);

  const texts = collectTexts(payload, entry);
  for (const t of texts) {
    await ensureFont(t.settings.FontArabicName);
    await ensureFont(t.settings.FontEnglishName);
  }
  texts.forEach((t) => drawText(ctx, t.value, t.settings));

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 1.0));
  if (!blob) return null;
  const name = fakeName();
  persistRendered(name, blob);
  return {
    imagesDesigned: [name],
    domainUrl: window.location.origin + "/images/shared",
    error: null,
    localUrl: URL.createObjectURL(blob),
  };
}
